// config/columnsConfig.js
// ⚠️ Los ids DEBEN coincidir con los campos que produce el pipeline `processedData`
// de InformesContainer.jsx (no con los nombres de la BD). El objeto key = column.id.
// El orden de los defaults respeta el de la tabla original (TablaInformes.jsx).

// Definición de todas las columnas disponibles
// `group`: grupo visual que replica los colores del header de la tabla original (TablaInformes.jsx)
//   - 'adenda'  → purple  (Exp. Adenda, Monto Adenda, Monto Total)
//   - 'avance'  → green   (Avance Físico / Financiero)
//   - 'monto'   → blue    (Monto Conveniado)
export const COLUMN_GROUP_CLASSES = {
  adenda: 'bg-purple-50 dark:bg-purple-900/20',
  avance: 'bg-green-50 dark:bg-green-900/20',
  monto: 'bg-blue-50 dark:bg-blue-900/20',
};

// Tabs del selector de columnas (replican los de las secciones del formulario de obra)
export const COLUMN_TABS = {
  CG: 'Características Generales',
  TRAM: 'Tramitación',
  SEG: 'Seguimiento de Obra',
};

export const AVAILABLE_COLUMNS = {
  // Columnas base (siempre visibles)
  id_partido_obra: { id: 'id_partido_obra', label: 'ID', default: true, required: true, role: 'all', tab: 'CG' },
  // Modalidad y los dos expedientes de adenda salieron de las "siempre visibles" por
  // pedido del usuario (2026-10-01): quedan apagadas por defecto pero se pueden prender
  // y apagar. Para que eso funcione NO alcanza con default:false, hay que sacarlas
  // también de ROLE_DEFAULT_COLUMNS.admin.columns, que es la lista que arma
  // ALWAYS_VISIBLE_IDS en TablaInformes_Dinamico.jsx.
  modalidad: { id: 'modalidad', label: 'Modalidad', default: false, required: false, role: 'all', tab: 'CG' },
  nroExpProyecto: { id: 'nroExpProyecto', label: 'Exp. Proyecto', default: true, required: true, role: 'all', tab: 'TRAM' },
  nroExpRendicion: { id: 'nroExpRendicion', label: 'Exp. Rendición', default: true, required: false, role: 'all', tab: 'TRAM' },
  nroExpAdenda1: { id: 'nroExpAdenda1', label: 'Exp. Adenda 1', default: false, required: false, role: 'all', group: 'adenda', tab: 'TRAM' },
  nroExpAdenda2: { id: 'nroExpAdenda2', label: 'Exp. Adenda 2', default: false, required: false, role: 'all', group: 'adenda', tab: 'TRAM' },
  nombreObra: { id: 'nombreObra', label: 'Nombre Obra', default: true, required: true, role: 'all', tab: 'CG' },

  // Información general
  programa_lineamiento: { id: 'programa_lineamiento', label: 'Programa', default: false, required: false, role: 'admin', tab: 'CG' },
  tipo_subsidio: { id: 'tipo_subsidio', label: 'Tipo Destinatario', default: false, required: false, role: 'all', tab: 'CG' },
  organismo_solicitante: { id: 'organismo_solicitante', label: 'Organización', default: false, required: false, role: 'all', tab: 'CG' },
  // Categoría de la ONG (ongs.categoria vía obras.ongs_id). Es la misma categoría
  // que usan los informes de ONGs (religiosa/deportiva/...). El filtro del mismo
  // nombre solo aparece cuando Tipo Destinatario = "Subsidio a ONG".
  ong_categoria: { id: 'ong_categoria', label: 'Categoría ONG', default: false, required: false, role: 'all', tab: 'CG' },
  partido_nombre: { id: 'partido_nombre', label: 'Partido', default: false, required: false, role: 'all', tab: 'CG' },
  estado: { id: 'estado', label: 'Estado', default: false, required: false, role: 'all', tab: 'CG' },
  beneficiarios: { id: 'beneficiarios', label: 'Beneficiarios', default: false, required: false, role: 'directora', tab: 'CG' },

  // Datos generales adicionales con dato en la mayoría de las obras.
  // OJO con los ids: el backend de /tabla_informes aliasea varios de estos campos,
  // así que NO llegan con el nombre de la columna de la BD (ver buildQueryObraInformes).
  tipo_proyecto: { id: 'tipo_proyecto', label: 'Tipo de Proyecto', default: false, required: false, role: 'admin', tab: 'CG' },
  tipo_contratacion: { id: 'tipo_contratacion', label: 'Tipo de Contratación', default: false, required: false, role: 'admin', tab: 'CG' },
  plazo_obra: { id: 'plazo_obra', label: 'Plazo de Obra', default: false, required: false, role: 'admin', tab: 'CG' },
  area_responsable: { id: 'area_responsable', label: 'Área Responsable', default: false, required: false, role: 'admin', tab: 'CG' },
  encargado_proyecto: { id: 'encargado_proyecto', label: 'Encargado de Proyecto', default: false, required: false, role: 'admin', tab: 'CG' },
  lc_21_localidad: { id: 'lc_21_localidad', label: 'Localidad', default: false, required: false, role: 'admin', tab: 'CG' },
  fecha_carga: { id: 'fecha_carga', label: 'Fecha de Carga', default: false, required: false, role: 'admin', tab: 'CG' },

  // Avances
  avanceFisico: { id: 'avanceFisico', label: 'Avance Físico %', default: false, required: false, role: 'all', group: 'avance', tab: 'SEG' },
  // Avance Financiero: compuesto (Convenio + Adendas en la misma celda, como la tabla original).
  // El CellRenderer lo renderiza con barras apiladas (Convenio + A1 + A2 cuando existen adendas)
  avanceFinanciero: { id: 'detalleConvenio', label: 'Avance Financiero %', default: false, required: false, role: 'all', composite: true, group: 'avance', tab: 'SEG' },
  ultimoPorcentajeObra: { id: 'ultimoPorcentajeObra', label: 'Avance Obra (Pagos) %', default: false, required: false, role: 'seguimiento', group: 'avance', tab: 'SEG' },

  // Montos
  monto_obra: { id: 'monto_obra', label: 'Monto Conveniado', default: false, required: false, role: 'all', group: 'monto', tab: 'CG' },
  montoAdenda1: { id: 'montoAdenda1', label: 'Monto Adenda1', default: false, required: false, role: 'all', group: 'adenda', tab: 'TRAM' },
  montoAdenda2: { id: 'montoAdenda2', label: 'Monto Adenda2', default: false, required: false, role: 'all', group: 'adenda', tab: 'TRAM' },
  montoTotal: { id: 'montoTotal', label: 'Monto Total', default: false, required: false, role: 'all', group: 'adenda', tab: 'TRAM' },

  // Seguimiento
  montoTotalPagadoGral: { id: 'montoTotalPagadoGral', label: 'Total Pagado', default: false, required: false, role: 'all', group: 'monto', tab: 'SEG' },
  ultimoPago: { id: 'ultimoPago', label: 'Último Pago', default: false, required: false, role: 'contable', tab: 'SEG' },
  // Mismo nombre en el formulario (formC_SeguimientoObra.jsx) para no confundirla
  // con 'observaciones_externas', que es de la pestaña Tramitación.
  observaciones: { id: 'observaciones', label: 'Observaciones del Seguimiento de Obra', default: false, required: false, role: 'seguimiento', tab: 'SEG' },

  // Fechas de caratulación de todos los expedientes en una sola celda (Proy · Rend · A1 · A2)
  fechasExpediente: { id: 'fechasExpediente', label: 'Fechas de Expediente', default: false, required: false, role: 'admin', tab: 'TRAM' },
  // Los tres textos de observaciones de la pestaña Tramitación, que antes se confundían
  // entre sí y con el del seguimiento de obra. Mismo nombre que en el formulario:
  // formB_Tramitacion.jsx. Son sparse (42, 125 y 22 de 822 con dato) pero opt-in.
  observacionesExternas: { id: 'observaciones_externas', label: 'Observaciones a la tramitación externa', default: false, required: false, role: 'admin', tab: 'TRAM' },
  observacionesReporte: { id: 'observaciones_reporte', label: 'Observaciones para reporte a la entidad solicitante', default: false, required: false, role: 'admin', tab: 'TRAM' },
  observacionesInternas: { id: 'observaciones_internas', label: 'Observaciones internas', default: false, required: false, role: 'admin', tab: 'TRAM' },
};

// Configuración de columnas por defecto según rol.
// Orden fijo (mismo que la tabla original): ID · Modalidad · Exp Proyecto · Exp Rendición ·
// Exp Adenda 1 · Exp Adenda 2 · Nombre Obra · Programa (admin) · Tipo Destinatario ·
// Organización · Partido · Estado · Avance Físico % · Avance Financiero % ·
// Monto Conveniado · Monto Adenda1 · Monto Adenda2 · Monto Total
//
// OJO: de esta lista solo se lee admin.columns para las "siempre visibles" de la tabla
// personalizada. Las demás entradas (user, contable, seguimiento, directora) son la
// referencia del orden de la tabla original y hoy no las usa nadie: los roles no-admin
// renderizan TablaInformes.jsx, que tiene sus columnas fijas en el JSX. Modalidad,
// Exp. Adenda 1 y Exp. Adenda 2 se sacaron SOLO de admin.columns.
const ORDER_COLUMNAS_BASE = [
  'id_partido_obra', 'modalidad', 'nroExpProyecto', 'nroExpRendicion',
  'nroExpAdenda1', 'nroExpAdenda2', 'nombreObra',
  'tipo_subsidio', 'organismo_solicitante', 'partido_nombre', 'estado',
  'avanceFisico', 'avanceFinanciero',
  'monto_obra', 'montoAdenda1', 'montoAdenda2', 'montoTotal'
];

export const ROLE_DEFAULT_COLUMNS = {
  user: { // Rol general (misma vista que la tabla original, sin Programa)
    columns: [...ORDER_COLUMNAS_BASE]
  },
  admin: { // admin → incluye Programa en su lugar original (después de Nombre Obra)
    // Las 15 de acá son las "siempre visibles" de la tabla personalizada de admin
    // (ALWAYS_VISIBLE_IDS se arma desde acá). Modalidad, Exp. Adenda 1 y Exp. Adenda 2
    // salen de esta lista y quedan como optativas: se apagan por defecto pero el usuario
    // las puede volver a prender. Sigue respetando el orden de la tabla original.
    columns: [
      'id_partido_obra', 'nroExpProyecto', 'nroExpRendicion',
      'nombreObra', 'programa_lineamiento',
      'tipo_subsidio', 'organismo_solicitante', 'partido_nombre', 'estado',
      'avanceFisico', 'avanceFinanciero',
      'monto_obra', 'montoAdenda1', 'montoAdenda2', 'montoTotal'
    ]
  },
  contable: {
    columns: [
      'id_partido_obra', 'modalidad', 'nroExpProyecto', 'nroExpRendicion',
      'nroExpAdenda1', 'nroExpAdenda2', 'nombreObra',
      'tipo_subsidio', 'organismo_solicitante', 'partido_nombre', 'estado',
      'avanceFisico', 'avanceFinanciero',
      'monto_obra', 'montoAdenda1', 'montoAdenda2', 'montoTotal',
      'ultimoPago'
    ]
  },
  seguimiento: {
    columns: [
      'id_partido_obra', 'modalidad', 'nroExpProyecto', 'nroExpRendicion',
      'nroExpAdenda1', 'nroExpAdenda2', 'nombreObra',
      'estado', 'avanceFisico', 'avanceFinanciero',
      'ultimoPorcentajeObra', 'observaciones'
    ]
  },
  directora: {
    columns: [
      'id_partido_obra', 'modalidad', 'nroExpProyecto', 'nroExpRendicion',
      'nroExpAdenda1', 'nroExpAdenda2', 'nombreObra', 'programa_lineamiento',
      'tipo_subsidio', 'organismo_solicitante', 'partido_nombre', 'estado',
      'avanceFisico', 'avanceFinanciero',
      'monto_obra', 'montoTotal', 'ultimoPorcentajeObra'
    ]
  }
};

// Columnas por defecto de la tabla personalizada, ÚNICAS para todos los roles.
// El selector "Personalizar columnas" antes era solo de admin y los no-admin veían
// TablaInformes.jsx (columnas fijas). Ahora todos usan la misma tabla dinámica, así que
// todos tienen que arrancar viendo lo mismo que veía admin: las 19 que resultan de sus
// 15 "siempre visibles" (admin.columns) más Modalidad, Exp. Adenda 1, Exp. Adenda 2 y
// Último Pago, que en el admin eran optativas y podía prender.
export const DEFAULT_COLUMNS_TABLA = [
  'id_partido_obra', 'modalidad', 'nroExpProyecto', 'nroExpRendicion',
  'nroExpAdenda1', 'nroExpAdenda2', 'nombreObra', 'programa_lineamiento',
  'tipo_subsidio', 'organismo_solicitante', 'partido_nombre', 'estado',
  'avanceFisico', 'avanceFinanciero',
  'monto_obra', 'montoAdenda1', 'montoAdenda2', 'montoTotal',
  'ultimoPago'
];

// Orden canónico de TODAS las columnas en la tabla.
//
// No es el orden de los grupos del selector: las 18 por defecto respetan el de la tabla
// original (TablaInformes.jsx), que NO sigue los tabs — los nroExp* van entre Modalidad
// y Nombre Obra. Reordenar por grupo movería columnas de la tabla original, así que acá
// las 18 por defecto quedan en su lugar relativo y las optativas se intercalan cerca de las suyas.
//
// Al activar una columna opcional entra en su posición, no al final.
export const COLUMN_ORDER = [
  // --- Identificación / Characteristics (tab Características Generales) ---
  'id_partido_obra',
  'modalidad',
  'nroExpProyecto',            // Trámitación, pero va acá en la tabla original
  'nroExpRendicion',
  'nroExpAdenda1',
  'nroExpAdenda2',
  'fechasExpediente',          // nueva: al lado de los números de expediente
  'nombreObra',
  'programa_lineamiento',
  'tipo_subsidio',
  'organismo_solicitante',
  'ong_categoria',
  'partido_nombre',
  'estado',
  'beneficiarios',
  'tipo_proyecto',             // nuevas
  'tipo_contratacion',
  'plazo_obra',
  'area_responsable',
  'encargado_proyecto',
  'lc_21_localidad',
  'fecha_carga',
  // --- Avances y montos (tab Seguimiento de Obra) ---
  'avanceFisico',
  'avanceFinanciero',
  'ultimoPorcentajeObra',
  'montoTotalPagadoGral',      // nueva: antes del bloque de montos
  'monto_obra',
  'montoAdenda1',
  'montoAdenda2',
  'montoTotal',
  'observacionesExternas',     // nuevas: textos largos, al final de su tab como 'observaciones'
  'observacionesReporte',
  'observacionesInternas',
  'ultimoPago',
  'observaciones',
];

// Índices de posición. Cada key entra también por su id real, porque no siempre coinciden
// (avanceFinanciero → detalleConvenio): si no, esa columna quedaría sin rankear y al final.
const ORDER_INDEX = (() => {
  const map = new Map();
  COLUMN_ORDER.forEach((key, i) => {
    map.set(key, i);
    const col = AVAILABLE_COLUMNS[key];
    if (col) map.set(col.id, i);
  });
  return map;
})();

// Grupos del selector, DERIVADOS de COLUMN_ORDER filtrando por el `tab` de cada columna.
// No es una lista aparte: el orden dentro de cada grupo es el mismo de la tabla, así que
// la pertenencia al tab y la posición no pueden desincronizarse.
export const COLUMN_GROUPS = Object.keys(COLUMN_TABS).reduce((acc, tabKey) => {
  acc[COLUMN_TABS[tabKey]] = COLUMN_ORDER.filter(key => AVAILABLE_COLUMNS[key]?.tab === tabKey);
  return acc;
}, {});

// Ordena columnas por COLUMN_ORDER. Las de id desconocido van al final pero NO se pierden,
// y entre ellas se conserva el orden previo (desempate por índice original).
export const sortColumnsByOrder = (cols = []) =>
  [...cols]
    .map((col, i) => ({ col, i }))
    .sort((a, b) => {
      const ra = ORDER_INDEX.get(a.col.id) ?? Number.MAX_SAFE_INTEGER;
      const rb = ORDER_INDEX.get(b.col.id) ?? Number.MAX_SAFE_INTEGER;
      return ra === rb ? a.i - b.i : ra - rb;
    })
    .map(({ col }) => col);

// Posición de una columna en el orden inicial. Sirve para insertar una columna nueva en
// su lugar sin tocar el orden de las que ya están visibles (que puede ser uno arrastrado).
export const getColumnRank = (id) => ORDER_INDEX.get(id) ?? Number.MAX_SAFE_INTEGER;

// Inserta `column` en la posición que le corresponde del orden inicial, respetando el
// orden que ya tienen las columnas visibles. Si el usuario arrastró columnas, eso manda.
export const insertAtCanonicalSpot = (cols = [], column) => {
  const rank = getColumnRank(column.id);
  const idx = cols.findIndex(c => getColumnRank(c.id) > rank);
  if (idx === -1) return [...cols, column];
  return [...cols.slice(0, idx), column, ...cols.slice(idx)];
};

// Columnas definidas sin tab, o sin entrar en COLUMN_ORDER. Es el modo de falla que
// se dio con 'Avance Financiero %': la columna existe pero no aparece con checkbox.
// Se avisa en desarrollo para que se note al agregar, no cuando alguien la busca.
if (process.env.NODE_ENV !== 'production') {
  const sinTab = Object.entries(AVAILABLE_COLUMNS).filter(([, c]) => c && !c.tab).map(([k]) => k);
  const sinOrden = Object.keys(AVAILABLE_COLUMNS).filter(k => !ORDER_INDEX.has(k));
  if (sinTab.length || sinOrden.length) {
    console.warn(
      '[configColumns] columnas mal registradas (no van a aparecer con checkbox):',
      { sinTab, sinOrden }
    );
  }
}
